import { BaseService } from "./BaseService";
import type {
  AnalyticsOperationsExportQuery,
  AnalyticsOperationsRangeQuery,
  OperationsApprovalTimeResponse,
  OperationsComplaintsResponse,
  OperationsOnHoldCanceledResponse,
  OperationsOnTimeRateResponse,
  OperationsProcessingTimeResponse,
  OperationsTotalRequestsResponse,
  OperationsUnderestimatedServicesResponse,
} from "@/types/analytics-operations";

class AnalyticsOperationsService extends BaseService {
  private readonly basePath = "/analytics/operations";

  getTotalRequests(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsTotalRequestsResponse> {
    return this.get<OperationsTotalRequestsResponse>(
      `${this.basePath}/total-requests`,
      query,
    );
  }

  getProcessingTime(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsProcessingTimeResponse> {
    return this.get<OperationsProcessingTimeResponse>(
      `${this.basePath}/processing-time`,
      query,
    );
  }

  getApprovalTime(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsApprovalTimeResponse> {
    return this.get<OperationsApprovalTimeResponse>(
      `${this.basePath}/approval-time`,
      query,
    );
  }

  getOnTimeRate(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsOnTimeRateResponse> {
    return this.get<OperationsOnTimeRateResponse>(
      `${this.basePath}/on-time-rate`,
      query,
    );
  }

  getComplaints(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsComplaintsResponse> {
    return this.get<OperationsComplaintsResponse>(
      `${this.basePath}/complaints`,
      query,
    );
  }

  getUnderestimatedServices(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsUnderestimatedServicesResponse> {
    return this.get<OperationsUnderestimatedServicesResponse>(
      `${this.basePath}/underestimated-services`,
      query,
    );
  }

  getOnHoldCanceled(
    query: AnalyticsOperationsRangeQuery,
  ): Promise<OperationsOnHoldCanceledResponse> {
    return this.get<OperationsOnHoldCanceledResponse>(
      `${this.basePath}/on-hold-canceled`,
      query,
    );
  }

  exportTotalRequests(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/total-requests/export`, query);
  }

  exportProcessingTime(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/processing-time/export`, query);
  }

  exportApprovalTime(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/approval-time/export`, query);
  }

  exportOnTimeRate(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/on-time-rate/export`, query);
  }

  exportComplaints(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/complaints/export`, query);
  }

  exportUnderestimatedServices(
    query: AnalyticsOperationsExportQuery,
  ): Promise<Blob> {
    return this.getBlob(
      `${this.basePath}/underestimated-services/export`,
      query,
    );
  }

  exportOnHoldCanceled(query: AnalyticsOperationsExportQuery): Promise<Blob> {
    return this.getBlob(`${this.basePath}/on-hold-canceled/export`, query);
  }
}

export const analyticsOperationsService = new AnalyticsOperationsService();
